import { getLocale } from '@/pages/cogo-marketing/i18n/locale-store'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

export type LivePlannerFailureReason =
  | 'configuration'
  | 'invalid-response'
  | 'scope-mismatch'
  | 'static-unsupported'
  | 'timeout'
  | 'unavailable'

export type LivePlannerProgressReason =
  | 'planning'
  | 'requirements'
  | 'validating'

const LIVE_PLANNER_PROGRESS_COPY: Record<
  LocaleCode,
  Record<LivePlannerProgressReason, string>
> = {
  en: {
    planning: 'Planning the app structure with the live planner',
    requirements: 'Checking every requested journey and guardrail',
    validating: 'Still validating the app plan against your brief',
  },
  ko: {
    planning: '실시간 플래너로 앱 구조를 계획하고 있습니다',
    requirements: '요청한 모든 사용자 여정과 가드레일을 확인하고 있습니다',
    validating: '요청 내용에 맞는 앱 계획인지 계속 검증하고 있습니다',
  },
  ja: {
    planning: 'ライブプランナーでアプリ構成を計画しています',
    requirements:
      '指定されたユーザージャーニーとガードレールをすべて確認しています',
    validating: 'ご依頼内容に照らしてアプリ計画の検証を続けています',
  },
  zh: {
    planning: '正在使用实时规划器规划应用结构',
    requirements: '正在检查所有要求的用户旅程和约束条件',
    validating: '仍在根据您的需求验证应用规划',
  },
  'zh-TW': {
    planning: '正在使用即時規劃器規劃應用程式結構',
    requirements: '正在檢查所有要求的使用者旅程與限制條件',
    validating: '仍在依照您的需求驗證應用程式規劃',
  },
}

const LIVE_PLANNER_FAILURE_COPY: Record<LocaleCode, Record<LivePlannerFailureReason, string>> = {
  en: {
    configuration:
      'App planning is not available in this Studio environment. No project was created. Please try again after the planning service is configured.',
    'invalid-response':
      'The app plan could not be verified safely. No project was created. Please try again.',
    'scope-mismatch':
      'The plan did not preserve the core scope of your request. No project was created. Review the request and try again.',
    'static-unsupported':
      'Static web-only generation is not available through the governed planner. No project was created. Choose a supported starter or revise the request.',
    timeout: 'App planning took too long. No project was created. Please try again.',
    unavailable:
      'App planning is temporarily unavailable. No project was created. Please try again.',
  },
  ko: {
    configuration:
      '현재 Studio 환경에서 앱 계획 서비스를 사용할 수 없습니다. 프로젝트를 만들지 않았습니다. 서비스 설정이 완료된 뒤 다시 시도해 주세요.',
    'invalid-response':
      '앱 계획을 안전하게 검증하지 못했습니다. 프로젝트를 만들지 않았습니다. 다시 시도해 주세요.',
    'scope-mismatch':
      '계획에서 요청의 핵심 범위를 확인하지 못했습니다. 프로젝트를 만들지 않았습니다. 요청 내용을 확인한 뒤 다시 시도해 주세요.',
    'static-unsupported':
      '현재 검증된 플래너에서는 정적 웹 전용 생성을 지원하지 않습니다. 프로젝트를 만들지 않았습니다. 지원되는 Starter를 선택하거나 요청을 수정해 주세요.',
    timeout: '앱 계획 시간이 초과되었습니다. 프로젝트를 만들지 않았습니다. 다시 시도해 주세요.',
    unavailable:
      '앱 계획 서비스를 일시적으로 사용할 수 없습니다. 프로젝트를 만들지 않았습니다. 다시 시도해 주세요.',
  },
  ja: {
    configuration:
      '現在の Studio 環境ではアプリ計画サービスを利用できません。プロジェクトは作成されませんでした。サービス設定後にもう一度お試しください。',
    'invalid-response':
      'アプリ計画を安全に検証できませんでした。プロジェクトは作成されませんでした。もう一度お試しください。',
    'scope-mismatch':
      '計画でリクエストの主要範囲を確認できませんでした。プロジェクトは作成されませんでした。リクエストを確認して、もう一度お試しください。',
    'static-unsupported':
      '現在の検証済みプランナーでは静的 Web 専用生成を利用できません。プロジェクトは作成されませんでした。対応する Starter を選ぶか、リクエストを修正してください。',
    timeout:
      'アプリ計画がタイムアウトしました。プロジェクトは作成されませんでした。もう一度お試しください。',
    unavailable:
      'アプリ計画サービスを一時的に利用できません。プロジェクトは作成されませんでした。もう一度お試しください。',
  },
  zh: {
    configuration: '当前 Studio 环境无法使用应用规划服务。未创建项目。请在服务配置完成后重试。',
    'invalid-response': '无法安全验证应用规划。未创建项目。请重试。',
    'scope-mismatch': '规划未保留请求的核心范围。未创建项目。请检查请求后重试。',
    'static-unsupported':
      '当前受控规划器不支持仅静态网页生成。未创建项目。请选择受支持的 Starter 或修改请求。',
    timeout: '应用规划超时。未创建项目。请重试。',
    unavailable: '应用规划服务暂时不可用。未创建项目。请重试。',
  },
  'zh-TW': {
    configuration:
      '目前的 Studio 環境無法使用應用程式規劃服務。未建立專案。請在服務設定完成後重試。',
    'invalid-response': '無法安全驗證應用程式規劃。未建立專案。請重試。',
    'scope-mismatch': '規劃未保留請求的核心範圍。未建立專案。請檢查請求後重試。',
    'static-unsupported':
      '目前受控規劃器不支援純靜態網頁生成。未建立專案。請選擇支援的 Starter 或修改請求。',
    timeout: '應用程式規劃逾時。未建立專案。請重試。',
    unavailable: '應用程式規劃服務暫時無法使用。未建立專案。請重試。',
  },
}

export function livePlannerFailureMessage(
  reason: LivePlannerFailureReason,
  locale: LocaleCode = getLocale(),
): string {
  return LIVE_PLANNER_FAILURE_COPY[locale][reason]
}

export function livePlannerProgressMessage(
  reason: LivePlannerProgressReason,
  locale: LocaleCode = getLocale(),
): string {
  return LIVE_PLANNER_PROGRESS_COPY[locale][reason]
}

// ───────────────────────────────────────────────────────────────────────────
// F-2 / F-9 — the rest of the planning progress line.
//
// `emitProgress` used to mix these two kinds of message: some came from
// LIVE_PLANNER_PROGRESS_COPY above (localized), others were English literals
// written inline at the call site. A Korean creator therefore read
// `실시간 플래너로 앱 구조를 계획하고 있습니다` and then `Planned 1 pages and 4
// blocks` in the same box. That box was invisible when the defect was
// measured; `ab2136640` restored it, so the audience went from nobody to
// everybody without a single string changing.
//
// F-9 (`Planned 1 pages`) is fixed HERE rather than by translating the broken
// English plural: `en` picks singular/plural, and the four CJK/Korean locales
// use their own counter forms, which do not pluralise at all.
// ───────────────────────────────────────────────────────────────────────────

export type LivePlannerStageReason =
  | 'checking-session'
  | 'mapping-plan'
  | 'validating-ontology'
  | 'simulating-actionflow'
  | 'generating-payload'

const LIVE_PLANNER_STAGE_COPY: Record<
  LocaleCode,
  Record<LivePlannerStageReason, string>
> = {
  en: {
    'checking-session': 'Checking Studio session',
    'mapping-plan': 'Mapping the plan into your app',
    'validating-ontology': 'Checking the app against the design rules',
    'simulating-actionflow': 'Checking that the behaviour can run',
    'generating-payload': 'Creating the pages, behaviour and data',
  },
  ko: {
    'checking-session': 'Studio 세션을 확인하고 있습니다',
    'mapping-plan': '계획을 앱 구조로 옮기고 있습니다',
    'validating-ontology': '디자인 규칙에 맞는지 확인하고 있습니다',
    'simulating-actionflow': '동작이 실행될 수 있는지 확인하고 있습니다',
    'generating-payload': '화면과 동작, 데이터를 만들고 있습니다',
  },
  ja: {
    'checking-session': 'Studio セッションを確認しています',
    'mapping-plan': '計画をアプリの構成に置き換えています',
    'validating-ontology': 'デザインのルールに沿っているか確認しています',
    'simulating-actionflow': '動作が実行できるか確認しています',
    'generating-payload': '画面と動作、データを作成しています',
  },
  zh: {
    'checking-session': '正在检查 Studio 会话',
    'mapping-plan': '正在把规划转换成应用结构',
    'validating-ontology': '正在检查是否符合设计规则',
    'simulating-actionflow': '正在检查行为是否可以运行',
    'generating-payload': '正在创建页面、行为和数据',
  },
  'zh-TW': {
    'checking-session': '正在檢查 Studio 工作階段',
    'mapping-plan': '正在把規劃轉換成應用程式結構',
    'validating-ontology': '正在檢查是否符合設計規則',
    'simulating-actionflow': '正在檢查行為是否可以執行',
    'generating-payload': '正在建立頁面、行為和資料',
  },
}

/** Count-bearing progress lines, authored per language rather than composed. */
interface LivePlannerCountCopy {
  planned: (pages: number, blocks: number) => string
  mapped: (pages: number, blocks: number) => string
  returnedIn: (seconds: number) => string
  creatingProject: (name: string) => string
}

const LIVE_PLANNER_COUNT_COPY: Record<LocaleCode, LivePlannerCountCopy> = {
  en: {
    planned: (pages, blocks) =>
      `Planned ${pages} ${pages === 1 ? 'page' : 'pages'} and ${blocks} ${blocks === 1 ? 'block' : 'blocks'}`,
    mapped: (pages, blocks) =>
      `Mapped ${pages} ${pages === 1 ? 'page' : 'pages'} and ${blocks} ${blocks === 1 ? 'block' : 'blocks'}`,
    returnedIn: (seconds) => `Live plan returned in ${seconds}s`,
    creatingProject: (name) => `Creating project for ${name}`,
  },
  ko: {
    planned: (pages, blocks) =>
      `화면 ${pages}개와 블록 ${blocks}개를 계획했습니다`,
    mapped: (pages, blocks) =>
      `화면 ${pages}개와 블록 ${blocks}개로 옮겼습니다`,
    returnedIn: (seconds) => `실시간 계획을 ${seconds}초 만에 받았습니다`,
    creatingProject: (name) => `${name} 프로젝트를 만들고 있습니다`,
  },
  ja: {
    planned: (pages, blocks) =>
      `${pages}画面と${blocks}ブロックを計画しました`,
    mapped: (pages, blocks) =>
      `${pages}画面と${blocks}ブロックに置き換えました`,
    returnedIn: (seconds) => `ライブ計画を${seconds}秒で受け取りました`,
    creatingProject: (name) => `${name} のプロジェクトを作成しています`,
  },
  zh: {
    planned: (pages, blocks) => `已规划 ${pages} 个页面和 ${blocks} 个区块`,
    mapped: (pages, blocks) => `已转换成 ${pages} 个页面和 ${blocks} 个区块`,
    returnedIn: (seconds) => `实时规划用了 ${seconds} 秒返回`,
    creatingProject: (name) => `正在创建「${name}」项目`,
  },
  'zh-TW': {
    planned: (pages, blocks) => `已規劃 ${pages} 個頁面和 ${blocks} 個區塊`,
    mapped: (pages, blocks) => `已轉換成 ${pages} 個頁面和 ${blocks} 個區塊`,
    returnedIn: (seconds) => `即時規劃用了 ${seconds} 秒回傳`,
    creatingProject: (name) => `正在建立「${name}」專案`,
  },
}

export function livePlannerStageMessage(
  reason: LivePlannerStageReason,
  locale: LocaleCode = getLocale(),
): string {
  return (LIVE_PLANNER_STAGE_COPY[locale] ?? LIVE_PLANNER_STAGE_COPY.en)[reason]
}

export function livePlannerPlannedMessage(
  pages: number,
  blocks: number,
  locale: LocaleCode = getLocale(),
): string {
  return (LIVE_PLANNER_COUNT_COPY[locale] ?? LIVE_PLANNER_COUNT_COPY.en).planned(
    pages,
    blocks,
  )
}

export function livePlannerMappedMessage(
  pages: number,
  blocks: number,
  locale: LocaleCode = getLocale(),
): string {
  return (LIVE_PLANNER_COUNT_COPY[locale] ?? LIVE_PLANNER_COUNT_COPY.en).mapped(
    pages,
    blocks,
  )
}

export function livePlannerReturnedInMessage(
  seconds: number,
  locale: LocaleCode = getLocale(),
): string {
  return (
    LIVE_PLANNER_COUNT_COPY[locale] ?? LIVE_PLANNER_COUNT_COPY.en
  ).returnedIn(seconds)
}

export function livePlannerCreatingProjectMessage(
  name: string,
  locale: LocaleCode = getLocale(),
): string {
  return (
    LIVE_PLANNER_COUNT_COPY[locale] ?? LIVE_PLANNER_COUNT_COPY.en
  ).creatingProject(name)
}
